import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Button,
  Image,
  Modal,
  Form as BootstrapForm,
  Alert,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import NumberFormat from "react-number-format";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  createTemplate,
  updateTemplate,
  getTemplate,
  testAIGeneration,
} from "../../../services/TemplatesServices";
import UserContext from "../../../contexts/UserContext";
import { Hash } from "../../../helpers/Helpers";
import {
  IconRobot,
  IconCheck,
  IconArrowLeft,
  IconDeviceFloppy,
} from "@tabler/icons-react";
import styled from "styled-components";

const WhatsAppTemplateForm = ({ agentId: propAgentId = null }) => {
  const { user, bottyData } = useContext(UserContext);
  const navigate = useNavigate();
  const { id, agentId: routeAgentId } = useParams(); // Para edição e agentId da rota
  const isEditing = !!id;

  // Usar agentId da rota se disponível, senão usar prop
  const agentId = routeAgentId || propAgentId;

  // Detecta se está no contexto do AgentHub
  const isInsideAgentHub =
    Boolean(routeAgentId) || window.location.pathname.includes("/agent-hub/");

  // Função para navegar de volta
  const handleBackNavigation = () => {
    if (isInsideAgentHub && agentId) {
      navigate(`/agents/${agentId}/hub`);
    } else {
      navigate("/templates");
    }
  };

  // Estados para gerenciar arquivos
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Estados para o simulador de chat
  const [isTyping, setIsTyping] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [notify, setNotify] = useState({ visible: false });

  // Estados para funcionalidades de IA
  const [testingAI, setTestingAI] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [testParams, setTestParams] = useState({ phone: "" });
  const [testParamErrors, setTestParamErrors] = useState({});
  const [messageTemplateType, setMessageTemplateType] = useState(null); // null, 'create' ou 'ai'
  const [showTypeSelection, setShowTypeSelection] = useState(true); // Controla a exibição da seleção inicial

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      name: "",
      action: "generic",
      message_type: "text",
      text_message: "",
      template_type: "whatsapp",
      use_ai_generation: false,
      ai_prompt_template: `A seguir você receberá as últimas 30 mensagens da conversa entre a IA e um lead no WhatsApp.

Analise o histórico para identificar o contexto, nível de interesse, objeções, intenção, tom e o motivo da pausa no contato.
**Se o nome do contato aparecer no histórico, utilize-o naturalmente na mensagem.

Com base nisso, gere UMA ÚNICA mensagem curta, clara, amigável e humana, com objetivo de reativar a conversa e incentivar resposta.
Você pode usar 1 ou 2 emojis no máximo, apenas se fizer sentido no contexto, para deixar o tom mais leve e natural.

A mensagem deve:
1. Ser personalizada e contextual;
2. Não repetir trechos literais da conversa;
3. Evitar tom genérico ou robótico;
4. Ter até 180 caracteres;
5. Manter tom cordial, profissional e próximo.

Retorne apenas a mensagem final, sem explicações.`,
      ai_history_limit: 30,
    },
  });

  // Carrega dados do template para edição
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      getTemplate(id)
.then((data) => {
  setValue("name", data.name);
  setValue("action", data.action || "generic");
  setValue("template_type", "whatsapp");

  // Determina o tipo de mídia baseado nos dados existentes
  if (data.media_file || data.media_url) {
    setValue("message_type", data.media_type);
    setPreview(data.media_file || data.media_url);
  } else if (data.document_file || data.document_url) {
    setValue("message_type", "document");
    setPreview(data.document_file || data.document_url);
  } else {
    setValue("message_type", "text");
  }

  // Define a mensagem de texto
  if (data.message && data.message.text) {
    setValue("text_message", data.message.text);
  }

  // Campos de IA
  setValue("use_ai_generation", data.use_ai_generation || false);
  setValue(
    "ai_prompt_template",
    data.ai_prompt_template ||
      "Você é um consultor comercial experiente. Analise o histórico de conversa com o cliente e gere uma mensagem de follow-up personalizada e persuasiva. A mensagem deve:\n\n1. Referenciar pontos específicos da conversa anterior\n2. Demonstrar genuíno interesse nas necessidades do cliente\n3. Oferecer valor adicional ou esclarecimentos relevantes\n4. Incluir uma chamada para ação clara e não invasiva\n5. Manter um tom profissional mas amigável\n\nBase sua resposta no contexto da conversa e nas necessidades identificadas do cliente."
  );
  setValue("ai_history_limit", data.ai_history_limit || 30);

  // Definir tipo de template baseado se usa IA
  setMessageTemplateType(data.use_ai_generation ? "ai" : "create");
  setShowTypeSelection(false); // Oculta seleção inicial para edição

  setIsLoading(false);
})
.catch(() => {
  setIsLoading(false);
  handleBackNavigation();
});
    }
  }, [isEditing, id, setValue, navigate]);

  const watchedValues = watch();
  const messageType = watch("message_type");
  const useAiGeneration = watch("use_ai_generation");

  // Simulação de digitação no chat
  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (watchedValues.text_message && watchedValues.text_message.trim()) {
      setIsTyping(true);
      setShowMessage(false);

      const timeout = setTimeout(() => {
setIsTyping(false);
setShowMessage(true);
      }, 1500);

      setTypingTimeout(timeout);
    } else {
      setIsTyping(false);
      setShowMessage(false);
    }

    return () => {
      if (typingTimeout) {
clearTimeout(typingTimeout);
      }
    };
  }, [watchedValues.text_message]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);

      // Cria preview se for imagem
      if (file.type.startsWith("image/")) {
const reader = new FileReader();
reader.onload = (e) => setPreview(e.target.result);
reader.readAsDataURL(file);
      } else {
setPreview(null);
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      // Campos básicos
      formData.append("name", data.name);
      formData.append("action", data.action);
      formData.append("template_type", "whatsapp");
      formData.append("user", user.user_id);
      formData.append("account_id", user.account_id);

      // Adicionar agent_id se fornecido (contexto do AgentHub)
      if (agentId) {
formData.append("agent", agentId);
      }

      // Estrutura da mensagem baseada no tipo
      let messageData = {};

      if (data.message_type === "text") {
messageData = {
  text: data.text_message,
};
      } else if (data.message_type === "image") {
messageData = {
  image: {},
  text: data.text_message || "",
};
formData.append("media_type", "image");
if (selectedFile) {
  formData.append("media_file", selectedFile);
}
      } else if (data.message_type === "audio") {
messageData = {
  audio: {},
};
formData.append("media_type", "audio");
if (selectedFile) {
  formData.append("media_file", selectedFile);
}
      } else if (data.message_type === "document") {
messageData = {
  document: {},
  text: data.text_message || "",
};
if (selectedFile) {
  formData.append("document_file", selectedFile);
  formData.append("document_name", selectedFile.name);
}
      }

      formData.append("message", JSON.stringify(messageData));

      // Campos de IA (baseado no tipo de template selecionado)
      const isAITemplate = messageTemplateType === "ai";
      formData.append("use_ai_generation", isAITemplate);
      if (isAITemplate && data.ai_prompt_template) {
formData.append("ai_prompt_template", data.ai_prompt_template);
formData.append("ai_history_limit", data.ai_history_limit || 30);
      }

      let response;
      if (isEditing) {
response = await updateTemplate(id, formData);
      } else {
response = await createTemplate(formData);
      }

      setNotify({
title: "Sucesso!",
message: `Template ${isEditing ? "atualizado" : "criado"} com sucesso!`,
type: "success",
visible: true,
      });

      // Redirect após sucesso
      setTimeout(() => {
handleBackNavigation();
      }, 1500);
    } catch (error) {
      console.error("Erro ao salvar template:", error);
      setNotify({
title: "Erro!",
message: `Erro ao ${isEditing ? "atualizar" : "criar"} template.`,
type: "error",
visible: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Função para validar parâmetros de teste
  const validateTestParams = () => {
    const errors = {};

    if (!testParams.phone || testParams.phone.trim() === "") {
      errors.phone = "Telefone é obrigatório";
    } else {
      const cleanPhone = testParams.phone.replace(/\D/g, "");
      if (cleanPhone.length < 10 || cleanPhone.length > 11) {
errors.phone = "Telefone deve ter DDD + número (10 ou 11 dígitos)";
      }
    }

    setTestParamErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Função para testar geração IA
  const handleTestAI = async () => {
    // Validar se há bottyData e phone
    if (!bottyData || !Array.isArray(bottyData) || !bottyData[0]?.phone) {
      setNotify({
        title: "Erro de configuração",
        message: "Dados de configuração não encontrados. Recarregue a página e tente novamente.",
        type: "error",
        visible: true,
      });
      return;
    }

    // Validar se há prompt configurado
    const currentPrompt = watch("ai_prompt_template");
    if (!currentPrompt || currentPrompt.trim() === "") {
      setNotify({
        title: "Prompt obrigatório",
        message: "Configure o prompt para IA antes de testar a geração.",
        type: "warning",
        visible: true,
      });
      return;
    }

    if (!validateTestParams()) {
      return;
    }

    setTestingAI(true);
    setShowTestModal(false);

    try {
      // Formatar telefone: remover caracteres não numéricos e adicionar código do Brasil (55)
      const cleanPhone = testParams.phone.replace(/\D/g, "");
      const formattedPhone = `${cleanPhone}`;

      let result;
      
      // UID é o phone do bottyData
      const uid = bottyData[0].phone;
      
      if (id) {
        // Template salvo - usar API normal
        result = await testAIGeneration(id, uid, formattedPhone);
      } else {
        // Template não salvo - usar API com dados do formulário
        const formData = {
          prompt: currentPrompt,
          history_limit: watch("ai_history_limit") || 30,
          phone: formattedPhone,
          template_name: watch("name") || "Template de teste"
        };
        
        // Usar API com dados temporários
        result = await testAIGeneration(null, uid, formattedPhone, formData);
      }
      
      setTestResult({
        type: "ai",
        data: result,
        success: result.success !== false, // Verificar se a API retornou success: false
      });
      
      setShowResultModal(true);
    } catch (error) {
      setTestResult({
        type: "ai",
        data: error.response?.data,
        success: false,
        error: error.response?.data?.message || "Erro ao testar geração IA",
      });
      setShowResultModal(true);
    } finally {
      setTestingAI(false);
    }
  };

  // Função para abrir modal de teste de IA
  const handleOpenTestModal = () => {
    setTestParams({ phone: "" });
    setTestParamErrors({});
    setShowTestModal(true);
  };

  // Função para fechar modais
  const handleCloseTestModal = () => {
    setShowTestModal(false);
    setTestParams({ phone: "" });
    setTestParamErrors({});
  };

  const handleCloseResultModal = () => {
    setShowResultModal(false);
    setTestResult(null);
  };

  if (isLoading) {
    return (
      <Container>
<div className="d-flex justify-content-center my-5">
  <div>Carregando...</div>
</div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
<HeaderLeft>
  <BackButton onClick={handleBackNavigation}>
    <IconArrowLeft size={20} />
  </BackButton>
  <div>
    <Title>{isEditing ? "Editar" : "Criar"} Template WhatsApp</Title>
    <Subtitle>Configure sua mensagem para envio automático</Subtitle>
  </div>
</HeaderLeft>
      </Header>

      <ContentContainer singleColumn={showTypeSelection || messageTemplateType === "ai"}>
<FormSection>
  {/* Tela de Seleção do Tipo de Mensagem */}
  {showTypeSelection && (
    <TypeSelectionContainer>
      <TypeSelectionHeader>
        <TypeSelectionTitle>📝 Que tipo de mensagem deseja criar?</TypeSelectionTitle>
        <TypeSelectionSubtitle>
          Escolha como deseja configurar sua mensagem de template
        </TypeSelectionSubtitle>
      </TypeSelectionHeader>

      <TypeOptionsContainer>
        <TypeOptionCard
          onClick={() => {
            setMessageTemplateType("create");
            setShowTypeSelection(false);
          }}
        >
          <TypeOptionIcon>✏️</TypeOptionIcon>
          <TypeOptionTitle>Criar Mensagem</TypeOptionTitle>
          <TypeOptionDescription>
            Crie uma mensagem personalizada com texto, imagem, áudio ou documento.
            Você terá controle total sobre o conteúdo da mensagem.
          </TypeOptionDescription>
          <TypeOptionFeatures>
            <Feature>• Texto personalizado</Feature>
            <Feature>• Anexar imagens, áudios ou documentos</Feature>
            <Feature>• Usar variáveis dinâmicas</Feature>
            <Feature>• Preview em tempo real</Feature>
          </TypeOptionFeatures>
        </TypeOptionCard>

        <TypeOptionCard
          onClick={() => {
            setMessageTemplateType("ai");
            setShowTypeSelection(false);
          }}
        >
          <TypeOptionIcon>🤖</TypeOptionIcon>
          <TypeOptionTitle>Mensagem com IA</TypeOptionTitle>
          <TypeOptionDescription>
            Gere mensagens automáticas e personalizadas baseadas no histórico
            da conversa do contato usando Inteligência Artificial.
          </TypeOptionDescription>
          <TypeOptionFeatures>
            <Feature>• Mensagens geradas automaticamente</Feature>
            <Feature>• Baseado no histórico da conversa</Feature>
            <Feature>• Personalização por prompt</Feature>
            <Feature>• Teste de geração em tempo real</Feature>
          </TypeOptionFeatures>
        </TypeOptionCard>
      </TypeOptionsContainer>
    </TypeSelectionContainer>
  )}

  {/* Formulário Principal - Aparece após seleção */}
  {!showTypeSelection && (
    <Form onSubmit={handleSubmit(onSubmit)}>
      {/* Botão para voltar à seleção */}
      <BackToSelectionContainer>
        <BackToSelectionButton
          type="button"
          onClick={() => {
            setShowTypeSelection(true);
            setMessageTemplateType(null);
          }}
        >
          ← Voltar à seleção de tipo
        </BackToSelectionButton>
        <SelectedTypeIndicator>
          {messageTemplateType === "create" ? (
            <>
              <TypeIcon>✏️</TypeIcon>
              Criando Mensagem Personalizada
            </>
          ) : (
            <>
              <TypeIcon>🤖</TypeIcon>
              Criando Mensagem com IA
            </>
          )}
        </SelectedTypeIndicator>
      </BackToSelectionContainer>

      <FormGroup>
        <Label>Nome do Template *</Label>
        <Input
          type="text"
          placeholder="Ex: Confirmação de Agendamento"
          {...register("name", { required: "Nome é obrigatório" })}
          error={!!errors.name}
        />
        {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
      </FormGroup>

      {/* Campos do template tradicional - aparecem apenas quando não é IA */}
      {messageTemplateType === "create" && (
        <>
          <FormGroup>
<Label>Tipo de Ação</Label>
<Select
  {...register("action", { required: "Ação é obrigatória" })}
  error={!!errors.action}
>
  <option value="generic">Genérico</option>
  <option value="confirmation_message">Confirmação</option>
  <option value="reminder_message">Lembrete</option>
  <option value="feedback">Feedback</option>
  <option value="tips_message">Dicas</option>
</Select>
{errors.action && (
  <ErrorText>{errors.action.message}</ErrorText>
)}
          </FormGroup>

          <FormGroup>
  <Label>Tipo de Mensagem</Label>
  <Select
    {...register("message_type")}
    onChange={(e) => {
      setValue("message_type", e.target.value);
      removeFile();
    }}
  >
    <option value="text">📝 Texto</option>
    <option value="image">🖼️ Imagem</option>
    <option value="audio">🎵 Áudio</option>
    <option value="document">📄 Documento</option>
  </Select>
</FormGroup>

{(messageType === "text" ||
  messageType === "image" ||
  messageType === "document") && (
  <FormGroup>
    <Label>Mensagem de Texto</Label>
    <TextArea
      rows={4}
      placeholder="Digite sua mensagem aqui..."
      {...register("text_message", {
required:
  messageType === "text"
    ? "Mensagem é obrigatória"
    : false,
      })}
      error={!!errors.text_message}
    />
    {errors.text_message && (
      <ErrorText>{errors.text_message.message}</ErrorText>
    )}
    <HelpText>
      Use variáveis como:{" "}
      {`{{nome_cliente}}, {{telefone}}, {{data_agendamento}}`}
    </HelpText>
  </FormGroup>
)}

{messageType !== "text" && (
  <FormGroup>
    <Label>
      {messageType === "image" && "Arquivo de Imagem"}
      {messageType === "audio" && "Arquivo de Áudio"}
      {messageType === "document" && "Arquivo de Documento"}
    </Label>
    <FileInputContainer>
      <FileInput
ref={fileInputRef}
type="file"
accept={
  messageType === "image"
    ? "image/*"
    : messageType === "audio"
    ? "audio/*"
    : ".pdf,.doc,.docx,.txt"
}
onChange={handleFileChange}
      />
      <FileButton
type="button"
onClick={() => fileInputRef.current?.click()}
      >
{selectedFile ? selectedFile.name : "Escolher Arquivo"}
      </FileButton>
      {selectedFile && (
<RemoveButton onClick={removeFile}>
  ❌ Remover
</RemoveButton>
      )}
    </FileInputContainer>
  </FormGroup>
          )}
        </>
      )}

      {/* Campos condicionais baseados no tipo selecionado */}
      {messageTemplateType === "ai" && (
      <AISection>
<AISectionTitle>
  🤖 Configuração da Inteligência Artificial
</AISectionTitle>

<FormGroup>
  <Label>Prompt para IA *</Label>
  <TextArea
    rows={10}
    {...register("ai_prompt_template", {
      required:
messageTemplateType === "ai"
  ? "Prompt é obrigatório para mensagens com IA"
  : false,
    })}
  />
  {errors.ai_prompt_template && (
    <ErrorText>{errors.ai_prompt_template.message}</ErrorText>
  )}
  <HelpText>
    Este prompt guiará a IA para gerar mensagens personalizadas
    baseadas no histórico da conversa
  </HelpText>
</FormGroup>

<FormGroup>
  <Label>Analisar últimas mensagens</Label>
  <Select {...register("ai_history_limit")}>
    <option value={10}>10 mensagens</option>
    <option value={20}>20 mensagens</option>
    <option value={30}>30 mensagens</option>
    <option value={40}>40 mensagens</option>
    <option value={50}>50 mensagens</option>
  </Select>
  <HelpText>
    Quantas mensagens do histórico a IA deve analisar para gerar
    a resposta
  </HelpText>
</FormGroup>

<TestButtonContainer>
  <TestButton
    type="button"
    onClick={handleOpenTestModal}
    disabled={testingAI || !bottyData?.[0]?.phone}
  >
    {testingAI ? (
<>
  <Spinner size="sm" className="me-2" />
  Testando Geração IA...
</>
    ) : (
"🧪 Testar Geração IA"
    )}
  </TestButton>
  {!bottyData?.[0]?.phone && (
    <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '8px', fontStyle: 'italic' }}>
      ⚠️ Nenhuma conta do whatsapp conectada. Conecte uma conta para testar a geração com IA.
    </div>
  )}
</TestButtonContainer>
      </AISection>
      )}

      <FormActions>
      <SecondaryButton type="button" onClick={handleBackNavigation}>
Cancelar
      </SecondaryButton>
      <PrimaryButton type="submit" disabled={isLoading}>
{isLoading ? (
  "Salvando..."
) : (
  <>
    <IconDeviceFloppy size={18} />
    {isEditing ? "Atualizar" : "Criar"} Template
  </>
)}
      </PrimaryButton>
      </FormActions>
    </Form>
  )}
</FormSection>

{!showTypeSelection && messageTemplateType === "create" && (
  <PreviewSection>
    <PreviewHeader>Preview do WhatsApp</PreviewHeader>
    <ChatContainer>
    <ChatBubble>
      {isTyping && (
<TypingIndicator>
  <TypingDot />
  <TypingDot />
  <TypingDot />
</TypingIndicator>
      )}

      {!isTyping && (
<>
  {/* Preview de Imagem */}
  {messageType === "image" && preview && (
    <WhatsAppMediaContainer>
      <WhatsAppImage src={preview} alt="Preview" />
      {watchedValues.text_message && (
<MediaCaption>
  {watchedValues.text_message}
</MediaCaption>
      )}
    </WhatsAppMediaContainer>
  )}

  {/* Preview de Áudio */}
  {messageType === "audio" && selectedFile && (
    <WhatsAppAudioContainer>
      <AudioIcon>▶️</AudioIcon>
      <AudioInfo>
<AudioName>{selectedFile.name}</AudioName>
<AudioDuration>0:30</AudioDuration>
      </AudioInfo>
      <AudioWaveform>
{[...Array(12)].map((_, i) => (
  <WaveformBar
    key={i}
    height={Math.random() * 20 + 5}
  />
))}
      </AudioWaveform>
    </WhatsAppAudioContainer>
  )}

  {/* Preview de Documento */}
  {messageType === "document" && selectedFile && (
    <>
      <WhatsAppDocumentContainer>
<DocumentIcon>📄</DocumentIcon>
<DocumentInfo>
  <DocumentName>{selectedFile.name}</DocumentName>
  <DocumentSize>
    {(selectedFile.size / 1024).toFixed(1)} KB
  </DocumentSize>
</DocumentInfo>
      </WhatsAppDocumentContainer>
      {watchedValues.text_message && (
<DocumentCaption>
  {watchedValues.text_message}
</DocumentCaption>
      )}
    </>
  )}

  {/* Preview de Texto ou placeholder */}
  {messageType === "text" && (
    <>
      {showMessage && watchedValues.text_message ? (
<MessageText>{watchedValues.text_message}</MessageText>
      ) : (
!watchedValues.text_message && (
  <PlaceholderText>
    Digite uma mensagem para ver o preview
  </PlaceholderText>
)
      )}
    </>
  )}

  {/* Placeholder para tipos de mídia sem arquivo */}
  {messageType === "image" && !preview && (
    <PlaceholderText>
      Selecione uma imagem para ver o preview
    </PlaceholderText>
  )}

  {messageType === "audio" && !selectedFile && (
    <PlaceholderText>
      Selecione um áudio para ver o preview
    </PlaceholderText>
  )}

  {messageType === "document" && !selectedFile && (
    <PlaceholderText>
      Selecione um documento para ver o preview
    </PlaceholderText>
  )}
</>
      )}
    </ChatBubble>
    </ChatContainer>
  </PreviewSection>
)}
      </ContentContainer>

      {notify.visible && (
<Notification type={notify.type}>
  <strong>{notify.title}</strong>
  <p>{notify.message}</p>
</Notification>
      )}

      {/* Modal para entrada de parâmetros de teste */}
      <Modal show={showTestModal} onHide={handleCloseTestModal} centered>
<Modal.Header closeButton>
  <Modal.Title>🧪 Testar Geração IA</Modal.Title>
</Modal.Header>
<Modal.Body>
  <Alert variant="info">
    <strong>Teste da geração de IA:</strong>
    <p className="mb-1">
      Informe o telefone de um contato para testar a geração automática
      de mensagem baseada no histórico.
    </p>
  </Alert>

  <BootstrapForm>
    <BootstrapForm.Group className="mb-3">
      <BootstrapForm.Label>Telefone do Contato *</BootstrapForm.Label>
      <NumberFormat
customInput={BootstrapForm.Control}
format="(##) #####-####"
mask="_"
placeholder="(11) 99999-9999"
value={testParams.phone}
onValueChange={(values) => {
  setTestParams({
    ...testParams,
    phone: values.formattedValue,
  });
}}
isInvalid={!!testParamErrors.phone}
      />
      <BootstrapForm.Control.Feedback type="invalid">
{testParamErrors.phone}
      </BootstrapForm.Control.Feedback>
      <BootstrapForm.Text className="text-muted">
Digite o telefone no formato (DDD) XXXXX-XXXX
      </BootstrapForm.Text>
    </BootstrapForm.Group>
  </BootstrapForm>
</Modal.Body>
<Modal.Footer>
  <Button variant="secondary" onClick={handleCloseTestModal}>
    Cancelar
  </Button>
  <Button variant="primary" onClick={handleTestAI} disabled={testingAI}>
    {testingAI ? (
      <>
<Spinner size="sm" className="me-2" />
Testando...
      </>
    ) : (
      "Executar Teste"
    )}
  </Button>
</Modal.Footer>
      </Modal>

      {/* Modal para exibir resultados do teste */}
      <Modal
show={showResultModal}
onHide={handleCloseResultModal}
size="lg"
centered
      >
<Modal.Header closeButton>
  <Modal.Title>
    {testResult?.type === "ai"
      ? "🤖 Resultado da Geração IA"
      : "📊 Resultado da Análise Follow-up"}
  </Modal.Title>
</Modal.Header>
<Modal.Body>
  {testResult?.success ? (
    <div>
      <Alert variant="success">
        <strong>✅ Teste executado com sucesso!</strong>
      </Alert>

      {testResult.type === "ai" ? (
<div>
  {testResult.data?.generated_message && (
    <div className="mb-3">
      <h6>Mensagem Gerada:</h6>
      <div className="p-3 bg-light rounded">
{testResult.data.generated_message}
      </div>
    </div>
  )}

  <div className="row">
    <div className="col-md-6">
      <small className="text-muted">
<strong>Histórico analisado:</strong>{" "}
{testResult.data?.history_analyzed || 0} mensagens
      </small>
    </div>
    <div className="col-md-6">
      <small className="text-muted">
<strong>Tamanho da mensagem:</strong>{" "}
{testResult.data?.message_length || 0} caracteres
      </small>
    </div>
  </div>

  {testResult.data?.original_prompt && (
    <div className="mt-3">
      <h6>Prompt Utilizado:</h6>
      <div className="p-3 bg-light rounded">
<small>{testResult.data.original_prompt}</small>
      </div>
    </div>
  )}
</div>
      ) : (
<div>
  {testResult.data?.analysis && (
    <div className="mb-3">
      <h6>Análise Gerada:</h6>
      <div className="p-3 bg-light rounded">
{testResult.data.analysis}
      </div>
    </div>
  )}

  <div className="row">
    <div className="col-md-6">
      <small className="text-muted">
<strong>Tamanho da análise:</strong>{" "}
{testResult.data?.analysis_length || 0} caracteres
      </small>
    </div>
  </div>

  {testResult.data?.prompt_used && (
    <div className="mt-3">
      <h6>Prompt Utilizado:</h6>
      <div className="p-3 bg-light rounded">
<small>{testResult.data.prompt_used}</small>
      </div>
    </div>
  )}
</div>
      )}
    </div>
  ) : (
    <div>
      <Alert variant="danger">
<strong>❌ Erro no teste</strong>
<p className="mb-0 mt-2">{testResult?.error || testResult?.data?.message || "Erro desconhecido"}</p>
      </Alert>

      {testResult?.data?.fallback && (
<div className="mt-3">
  <h6>Fallback:</h6>
  <div className="p-3 bg-warning-light rounded">
    {testResult.data.fallback}
  </div>
</div>
      )}
    </div>
  )}
</Modal.Body>
<Modal.Footer>
  <Button variant="secondary" onClick={handleCloseResultModal}>
    Fechar
  </Button>
</Modal.Footer>
      </Modal>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background: var(--background);
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: var(--surface);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
    color: var(--text-primary);
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 4px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ContentContainer = styled.div`
  display: grid;
  grid-template-columns: ${props => props.singleColumn ? '1fr' : '1fr 400px'};
  gap: 32px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid
    ${(props) => (props.error ? "var(--danger)" : "var(--border-color)")};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid
    ${(props) => (props.error ? "var(--danger)" : "var(--border-color)")};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid
    ${(props) => (props.error ? "var(--danger)" : "var(--border-color)")};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;
  color: var(--text-primary);
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ErrorText = styled.span`
  color: var(--danger);
  font-size: 12px;
`;

const HelpText = styled.span`
  color: var(--text-muted);
  font-size: 12px;
`;

const FileInputContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const FileInput = styled.input`
  display: none;
`;

const FileButton = styled.button`
  background: var(--surface);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  color: var(--text-secondary);

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
  }
`;

const RemoveButton = styled.button`
  background: var(--danger);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--danger-hover);
  }
`;

const FormActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid var(--border-color);

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SecondaryButton = styled.button`
  background: var(--surface);
  color: var(--text-secondary);
  border: 2px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    border-color: var(--border-color-hover);
  }
`;

const PrimaryButton = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PreviewSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-sm);
  height: fit-content;
  position: sticky;
  top: 20px;

  @media (max-width: 1024px) {
    position: static;
  }
`;

const PreviewHeader = styled.h3`
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  text-align: center;
`;

const ChatContainer = styled.div`
  background: #e5ddd5;
  border-radius: 12px;
  padding: 20px;
  min-height: 200px;
  background-image: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="chat-bg" patternUnits="userSpaceOnUse" width="100" height="100"><circle fill="%23ffffff" fill-opacity="0.05" cx="50" cy="50" r="1"/></pattern></defs><rect width="100" height="100" fill="url(%23chat-bg)"/></svg>');
`;

const ChatBubble = styled.div`
  background: #dcf8c6;
  border-radius: 12px 12px 4px 12px;
  padding: 12px 16px;
  max-width: 85%;
  margin-left: auto;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    right: -8px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-top-color: #dcf8c6;
    border-bottom: 0;
    margin-bottom: -8px;
  }
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const TypingDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
  animation: typing 1.4s infinite ease-in-out;

  &:nth-child(1) {
    animation-delay: -0.32s;
  }
  &:nth-child(2) {
    animation-delay: -0.16s;
  }

  @keyframes typing {
    0%,
    80%,
    100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const MessageText = styled.p`
  color: #333;
  margin: 0;
  line-height: 1.4;
  word-wrap: break-word;
`;

const PlaceholderText = styled.p`
  color: #666;
  margin: 0;
  font-style: italic;
  opacity: 0.7;
`;

// Componentes para Preview de Mídia
const WhatsAppMediaContainer = styled.div`
  margin-bottom: 8px;
`;

const WhatsAppImage = styled.img`
  width: 100%;
  max-width: 250px;
  border-radius: 8px;
  margin-bottom: ${(props) => (props.hasCaption ? "8px" : "0")};
  object-fit: cover;
`;

const MediaCaption = styled.p`
  color: #333;
  margin: 8px 0 0 0;
  line-height: 1.4;
  word-wrap: break-word;
  font-size: 14px;
`;

// Componentes para Preview de Áudio
const WhatsAppAudioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 8px 12px;
  min-height: 60px;
`;

const AudioIcon = styled.div`
  font-size: 20px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 50%;
`;

const AudioInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AudioName = styled.div`
  font-size: 12px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AudioDuration = styled.div`
  font-size: 11px;
  color: #888;
  margin-top: 2px;
`;

const AudioWaveform = styled.div`
  display: flex;
  align-items: end;
  gap: 1px;
  height: 20px;
`;

const WaveformBar = styled.div`
  width: 2px;
  background: #4a90e2;
  border-radius: 1px;
  height: ${(props) => props.height}px;
  opacity: 0.7;
`;

// Componentes para Preview de Documento
const WhatsAppDocumentContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px;
`;

const DocumentIcon = styled.div`
  font-size: 24px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const DocumentInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocumentName = styled.div`
  font-size: 14px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
`;

const DocumentSize = styled.div`
  font-size: 12px;
  color: #666;
`;

const DocumentCaption = styled.p`
  color: #333;
  margin: 8px 0 0 0;
  line-height: 1.4;
  word-wrap: break-word;
  font-size: 14px;
`;

const Notification = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: ${(props) =>
    props.type === "success" ? "var(--success)" : "var(--danger)"};
  color: white;
  padding: 16px 20px;
  border-radius: 8px;
  box-shadow: var(--shadow-lg);
  z-index: 1000;
  animation: slideIn 0.3s ease;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  strong {
    display: block;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

// Componentes para seção de IA
const AISection = styled.div`
  background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
  border: 1px solid #e1e5ff;
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
`;

const AISectionTitle = styled.h3`
  color: #4f46e5;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TestButtonContainer = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: flex-start;
`;

const TestButton = styled.button`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


// Novos componentes para a seleção de tipo
const TypeSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0px 20px;
  min-height: 400px;
`;

const TypeSelectionHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  max-width: 600px;
`;

const TypeSelectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px 0;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const TypeSelectionSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0;
`;

const TypeOptionsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  max-width: 800px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const TypeOptionCard = styled.div`
  background: white;
  border: 2px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    border-color: var(--primary);
    box-shadow: 0 8px 25px rgba(99, 102, 241, 0.15);
    transform: translateY(-2px);
  }
`;

const TypeOptionIcon = styled.div`
  font-size: 48px;
  margin-bottom: 20px;
`;

const TypeOptionTitle = styled.h3`
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const TypeOptionDescription = styled.p`
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const TypeOptionFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const Feature = styled.li`
  color: var(--text-secondary);
  font-size: 13px;
  margin: 6px 0;
  display: flex;
  align-items: center;
`;

const BackToSelectionContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px 20px;
  background: var(--surface);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
`;

const BackToSelectionButton = styled.button`
  background: none;
  border: none;
  color: var(--primary);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(99, 102, 241, 0.1);
  }
`;

const SelectedTypeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
`;

const TypeIcon = styled.span`
  font-size: 16px;
`;

export default WhatsAppTemplateForm;
